import React from 'react';
import {
  Box, ButtonBase, CircularProgress, IconButton, InputAdornment, ListSubheader, MenuItem, MenuList, Popover,
  TextField, Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';

export interface SelectOption {
  value: string;
  label: string;
  /** Options sharing a group are listed together under its name. */
  group?: string;
  /** A line under the option explaining it, e.g. a metric's definition. */
  description?: string | null;
  /** A short figure at the end of the row, e.g. "13 legs". */
  meta?: string | null;
}

export interface SelectMenuProps {
  /** Names the choice, shown before the value: "Metric  Approval rate". */
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** Show a search box. Defaults to on when there are more than 10 options. */
  searchable?: boolean;
  disabled?: boolean;
  /**
   * Options come from the caller for what is typed — fetched from a server,
   * say — so the list is shown as given rather than filtered here, and the
   * search box is always there.
   */
  onSearchChange?: (query: string) => void;
  /** Called as the list opens, e.g. to fetch options before anything is typed. */
  onOpen?: () => void;
  /** Options are being fetched. */
  loading?: boolean;
  /**
   * Offer what was typed as a value in its own right, for a choice whose list
   * is not every possible answer — an airport code nobody has flown to yet.
   * Return the value to use, or null when the text is not one.
   */
  customValue?: (query: string) => string | null;
  /** Shown in the box when `value` is not among `options`, e.g. while they reload. Defaults to the value. */
  valueLabel?: string;
  /** Shown in the box with no value. */
  placeholder?: string;
  /**
   * What the search box says when empty. Defaults to "Search <label>", which
   * suits a list of names and not a box where a number is typed — or a label
   * whose capitals matter, like MQLs.
   */
  searchPlaceholder?: string;
  /**
   * A last row in the list where a value is typed: "Use [ 40 ]".
   *
   * For a list whose options are the usual answers rather than all of them —
   * a minimum, a threshold, a number of days. The reader sees the box among
   * the choices, where a search field above the list would have them hunting
   * for a value that was never in it.
   */
  customEntry?: {
    /** The word before the box. Defaults to "Use". */
    label?: string;
    placeholder?: string;
    /** What was typed, as a value — or null when it is not one. */
    parse: (typed: string) => string | null;
  };
  /**
   * `small` (default) — for a panel header or a toolbar of small controls.
   * `medium` — for a page's main input, level with a medium `DateRangePicker`.
   */
  size?: 'small' | 'medium';
  /** Which edge the list lines up with: `end` (default) for the right of a header, `start` for a toolbar's left. */
  align?: 'start' | 'end';
  /**
   * Whether the list is open, when the page decides — to open the next choice
   * once one is made, say. Leave out and the select opens and closes itself.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A compact single-choice select for a panel header or toolbar.
 *
 * Reads "Metric  Approval rate ▾" in one small box. Opens a list that can be
 * grouped, searched, and carry a line explaining each option — so what a
 * metric divides by can be answered at the moment of choosing it.
 *
 * With `onSearchChange` its options can come from a server as you type, and
 * with `customValue` it takes a value that is not in the list — an airport
 * code, say.
 *
 * For two to four short options that are all worth seeing at once, use
 * `SegmentedControl`. For picking several values, use `FilterSelect`.
 */
export function SelectMenu({
  label, value, options, onChange, searchable, disabled,
  onSearchChange, onOpen, loading = false, customValue, valueLabel, placeholder, searchPlaceholder,
  customEntry,
  size = 'small', align = 'end', open, onOpenChange,
}: SelectMenuProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [ownOpen, setOwnOpen] = React.useState(false);
  const isOpen = open ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (open === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  // Opened from outside counts as opening too, e.g. to fetch its options.
  React.useEffect(() => {
    if (isOpen) onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const [query, setQuery] = React.useState('');
  const remote = Boolean(onSearchChange);
  const showSearch = remote || (searchable ?? options.length > 10);
  const selected = options.find((o) => o.value === value);
  const shown = selected?.label ?? (value ? valueLabel ?? value : null);

  const [typed, setTyped] = React.useState('');
  const entryValue = customEntry && typed.trim() ? customEntry.parse(typed.trim()) : null;

  const needle = query.trim().toLowerCase();
  const visible = needle && !remote
    ? options.filter((o) => `${o.label} ${o.description ?? ''} ${o.group ?? ''}`.toLowerCase().includes(needle))
    : options;
  const custom = customValue && query.trim() ? customValue(query.trim()) : null;
  const offerCustom = custom !== null && !visible.some((o) => o.value === custom);

  const grouped: { group: string | null; items: SelectOption[] }[] = [];
  visible.forEach((option) => {
    const group = option.group ?? null;
    const bucket = grouped.find((g) => g.group === group);
    if (bucket) bucket.items.push(option);
    else grouped.push({ group, items: [option] });
  });

  const search = (next: string) => {
    setQuery(next);
    onSearchChange?.(next);
  };
  const close = () => {
    setOpen(false);
    if (query) search('');
  };
  const choose = (next: string) => {
    onChange(next);
    close();
  };

  return (
    <>
      <ButtonBase
        ref={buttonRef}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label}: ${shown ?? 'none'}`}
        sx={(t) => ({
          ...t.typography.body2,
          height: size === 'medium' ? t.ds.sizes.control.md : t.ds.sizes.control.sm,
          maxWidth: '100%',
          minWidth: 0,
          gap: 0.75,
          pl: size === 'medium' ? 1.5 : 1.25,
          pr: size === 'medium' ? 1 : 0.5,
          borderRadius: `${t.ds.radius.sm}px`,
          border: `1px solid ${t.tokens.border.default}`,
          bgcolor: t.tokens.surface.raised,
          transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          '&:hover': { borderColor: t.tokens.border.strong },
          '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}`, outlineOffset: 1 },
        })}
      >
        <Box component="span" sx={(t) => ({ flexShrink: 0, color: t.tokens.text.tertiary })}>
          {label}
        </Box>
        {/* One width, whatever is picked. Every option's label sits in the
            same grid cell, invisible, so the button is as wide as the longest
            one and a header does not reflow when the choice changes — a long
            scenario name used to push the panel's controls onto their own
            line, a short one pulled them back. Capped, so a long list does not
            make a wide button; a name past the cap ends in an ellipsis. */}
        <Box
          component="span"
          sx={{ display: 'inline-grid', minWidth: 0, maxWidth: 360, textAlign: 'left' }}
        >
          {options.length <= 40 && options.map((o) => (
            <Box
              key={o.value}
              component="span"
              aria-hidden
              sx={{
                gridArea: '1 / 1', visibility: 'hidden', whiteSpace: 'nowrap', fontWeight: 600,
                overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
              }}
            >
              {o.label}
            </Box>
          ))}
          <Box
            component="span"
            sx={(t) => ({
              gridArea: '1 / 1',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: shown ? t.tokens.text.primary : t.tokens.text.tertiary,
              fontWeight: shown ? 600 : 400,
            })}
          >
            {shown ?? placeholder ?? '—'}
          </Box>
        </Box>
        <ExpandMoreRounded fontSize="small" sx={(t) => ({ flexShrink: 0, color: t.tokens.text.tertiary })} />
      </ButtonBase>

      <Popover
        open={isOpen && Boolean(buttonRef.current)}
        anchorEl={buttonRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: align === 'start' ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: align === 'start' ? 'left' : 'right' }}
        slotProps={{
          paper: {
            'aria-label': label,
            sx: (t: Theme) => ({
              mt: 0.5,
              width: 340,
              maxWidth: 'calc(100vw - 32px)',
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            }),
          },
        }}
      >
        {showSearch && (
          <Box sx={{ px: 1, pt: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}`}
              value={query}
              onChange={(e) => search(e.target.value)}
              onKeyDown={(e) => {
                // Enter takes the value typed exactly, else the first match.
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const exact = visible.find((o) => o.value === custom);
                const pick = exact?.value ?? visible[0]?.value ?? custom;
                if (pick) choose(pick);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: loading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={14} aria-label="Loading" />
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
          </Box>
        )}

        <MenuList
          dense
          autoFocusItem={!showSearch}
          role="listbox"
          sx={{ maxHeight: 400, overflowY: 'auto', py: 0.5 }}
        >
          {offerCustom && custom && (
            <MenuItem role="option" aria-selected={false} onClick={() => choose(custom)} sx={{ py: 0.75 }}>
              <Typography variant="body2">
                Use <strong>{custom}</strong>
              </Typography>
            </MenuItem>
          )}
          {loading && visible.length === 0 && <MenuItem disabled>Looking…</MenuItem>}
          {!loading && visible.length === 0 && !offerCustom && <MenuItem disabled>No matches</MenuItem>}
          {grouped.flatMap(({ group, items }) => [
            ...(group
              ? [
                  <ListSubheader
                    key={`group-${group}`}
                    sx={(t) => ({
                      ...t.typography.overline,
                      lineHeight: 2.6,
                      color: t.tokens.text.tertiary,
                      bgcolor: t.tokens.surface.overlay,
                    })}
                  >
                    {group}
                  </ListSubheader>,
                ]
              : []),
            ...items.map((option) => {
              const isSelected = option.value === value;
              return (
                <MenuItem
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  selected={isSelected}
                  onClick={() => choose(option.value)}
                  sx={{ alignItems: 'flex-start', gap: 1, py: 0.75, whiteSpace: 'normal' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                      {option.label}
                    </Typography>
                    {option.description && (
                      <Typography variant="caption" sx={(t) => ({ display: 'block', color: t.tokens.text.tertiary })}>
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                  {option.meta && (
                    <Typography
                      variant="caption"
                      sx={(t) => ({ mt: 0.25, flexShrink: 0, color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}
                    >
                      {option.meta}
                    </Typography>
                  )}
                  <CheckRounded
                    fontSize="small"
                    sx={(t) => ({ mt: 0.25, color: t.tokens.text.accent, visibility: isSelected ? 'visible' : 'hidden' })}
                  />
                </MenuItem>
              );
            }),
          ])}
          {customEntry && (
            <Box
              component="li"
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderTop: `1px solid ${t.tokens.border.subtle}`,
                mt: 0.5,
              })}
            >
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                {customEntry.label ?? 'Use'}
              </Typography>
              <TextField
                size="small"
                value={typed}
                placeholder={customEntry.placeholder}
                onChange={(e) => setTyped(e.target.value)}
                // The list listens for keys to jump between options; without
                // this, typing in the box moves the highlight instead.
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (entryValue !== null) choose(entryValue);
                }}
                inputProps={{ 'aria-label': `${customEntry.label ?? 'Use'} ${label}` }}
                sx={{ width: 120 }}
              />
              <IconButton
                size="small"
                aria-label={`Use this ${label.toLowerCase()}`}
                disabled={entryValue === null}
                onClick={() => entryValue !== null && choose(entryValue)}
              >
                <CheckRounded fontSize="small" />
              </IconButton>
            </Box>
          )}
        </MenuList>
      </Popover>
    </>
  );
}
